import api from './api-service';

/**
 * Every call reads its own status instead of letting a refusal reach the shared response
 * interceptor, which treats any 401 as an expired session: it clears the cookie, toasts "please
 * try logging in again" and reloads. A mistyped code would otherwise sign the person out in the
 * middle of setting up the factor meant to protect them. The API answers 422 for a bad code or
 * password for the same reason; this is the belt to that braces.
 *
 * @param {Promise} request
 * @param {string} unavailable What to say when the server, rather than the input, is the problem.
 * @returns {Promise<Object>}
 */
const read = (request, unavailable) => {
	return request.then(({ data, status } = {}) => {
		if(status >= 200 && status < 300) {
			return data;
		}

		// A 4xx names something the person can act on -- a wrong code, a factor already set up.
		// A 5xx carries an error meant for our logs, so it gets replaced.
		throw new Error(status >= 500 ? unavailable : (data?.error || unavailable));
	});
};

const options = { validateStatus: () => true };

/**
 * Setting up and taking down an authenticator-app second factor.
 *
 * Every call here acts on the signed-in user's own account — the API resolves who that is from
 * the request credentials and takes no user parameter, so there is nothing to pass and no way to
 * aim one of these at somebody else. The exception is `reset`, which is the administrator's
 * lost-device path and names its target.
 *
 * The secret and the recovery codes each exist in exactly one response and are never fetchable
 * again. Nothing here caches them, and callers are expected to hand them straight to the person.
 */
/**
 * What the API answers when an account with nothing to type -- no password the sign-in would
 * accept, no passkey -- asks to set up an authenticator without a fresh sign-in behind it. Mirrors
 * `TwoFactor::REAUTHENTICATION_REQUIRED` on the API.
 */
export const REAUTHENTICATION_REQUIRED = 'Sign in again before setting up an authenticator';

const TwoFactorService = {
	/**
	 * What the account has: whether an authenticator is set up, how many recovery codes are
	 * left, whether the studio enforces two-factor, what enrolling will ask the person to prove
	 * themselves with (`confirmWith`: `passkey`, `password` or `none`), and whether the
	 * deployment can do this at all.
	 */
	status: () => {
		return read(
			api.client.get('/two-factor', options),
			'Two-factor settings are unavailable right now'
		);
	},

	/**
	 * Begin setup. Returns `{ secret, uri }` — the only time the secret is readable.
	 *
	 * The password is sent because the API asks for it: a session token alone should not be able
	 * to attach a second factor that outlives a password change. Accounts with no password, which
	 * means people who have only ever used Google or Microsoft, send nothing and are not asked.
	 */
	enroll: ({ password } = {}) => {
		return read(
			api.client.post('/two-factor/enroll', password ? { password } : {}, options),
			'Two-factor authentication is not available right now'
		);
	},

	/**
	 * Finish setup by proving a code. Returns the ten recovery codes, once.
	 */
	confirm: ({ code }) => {
		return read(
			api.client.post('/two-factor/confirm', { code }, options),
			'Could not complete setup right now'
		);
	},

	/**
	 * Remove the authenticator. Needs a current code, or one of the recovery codes — not a
	 * password, since a password is what somebody holding a stolen session is least likely to be
	 * missing, and removing the factor is exactly what they would want to do.
	 */
	disable: ({ code, recoveryCode } = {}) => {
		return read(
			api.client.post(
				'/two-factor/disable',
				recoveryCode ? { recoveryCode } : { code },
				options
			),
			'Could not turn off two-factor authentication right now'
		);
	},

	/**
	 * Issue a fresh set of recovery codes, retiring the old ones. Needs a code for the same
	 * reason `disable` does.
	 */
	replaceRecoveryCodes: ({ code, recoveryCode } = {}) => {
		return read(
			api.client.post(
				'/two-factor/recovery',
				recoveryCode ? { recoveryCode } : { code },
				options
			),
			'Could not issue new recovery codes right now'
		);
	},

	/**
	 * Administrators only: clear somebody else's lost device so they can enroll again.
	 */
	reset: ({ user }) => {
		return read(
			api.client.post('/two-factor/reset', { user }, options),
			'Could not reset that authenticator right now'
		);
	}
};

export default TwoFactorService;
